/**
 * Árbol de navegación de la app — una sola fuente.
 *
 * Vive aquí y no dentro del Sidebar porque lo consume también el buscador
 * global (⌘K): el bloque "Ir a…" sale de ESTE array, así que una entrada nueva
 * en el menú aparece en el buscador sin tocar nada más. Duplicar la lista sería
 * garantizar que se desincronizan.
 */
import type { LucideIcon } from 'lucide-react'
import {
  LayoutGrid, Users, Target, Briefcase, FileText, FileSignature, Mail, Calendar,
  CheckSquare, Receipt, Coins, Wallet, Landmark, Boxes, Scale, Calculator,
  BookOpen, BookCheck, ShieldCheck, TrendingUp, Sparkles, ScanText, UserCircle,
  Package, Building2, Send, Plug, RotateCw, Megaphone, ClipboardList, Ban,
} from 'lucide-react'

export type Accent =
  | 'toga'
  | 'cyan'
  | 'purple'
  | 'pink'
  | 'orange'
  | 'rose'
  | 'emerald'
  | 'indigo'
  | 'amber'
  | 'sky'
  | 'blue'
  | 'fuchsia'

export interface NavItemDef {
  to: string
  label: string
  labelKey: string
  icon: LucideIcon
  accent: Accent
  /** Solo visible para ADMIN (subsecciones de Ajustes). */
  adminOnly?: boolean
  /** Visible para el rol CONTABILIDAD (gestoría): whitelist — el resto de
   *  items se le ocultan. El enforcement real está en el backend (403). */
  contabilidad?: boolean
  /** Cuelga visualmente del item anterior: texto e icono más pequeños y
   *  sangrado. NO es un nivel plegable — es solo tipografía, que es justo lo
   *  que hace legible una sección larga sin añadir un clic. */
  child?: boolean
  /** Enlace EXTERNO real: se renderiza como <a> (navegación del navegador),
   *  no como <Link> de react-router. `to` se usa como href y se abre en una
   *  pestaña nueva.
   *
   *  Hoy NO lo usa ninguna entrada: el único que había —el SSO a JR2 MKT— se
   *  mudó al icono de la barra superior (ver `lib/marketing.ts`) cuando el
   *  menú pasó a apuntar a la herramienta de marketing propia. Se mantiene
   *  porque Sidebar y el buscador ⌘K saben renderizarlo, así que colgar otra
   *  app externa del menú es añadir una línea aquí. */
  external?: boolean
}

export interface NavGroup {
  label: string
  labelKey: string
  items: NavItemDef[]
}

export const groups: NavGroup[] = [
  {
    label: 'PRINCIPAL',
    labelKey: 'sidebar.groups.principal',
    items: [
      { to: '/dashboard', label: 'Dashboard', labelKey: 'sidebar.items.dashboard', icon: LayoutGrid, accent: 'toga' },
      { to: '/inbox', label: 'Buzón', labelKey: 'sidebar.items.inbox', icon: Mail, accent: 'sky' },
      { to: '/ai', label: 'Asistente IA', labelKey: 'sidebar.items.aiAssistant', icon: Sparkles, accent: 'purple' },
    ],
  },
  {
    // Grupo propio y replegable, como GESTIÓN: con cuatro pantallas ya no cabe
    // colgando de PRINCIPAL sin comerse media barra. La herramienta de HOY
    // (JR2 MKT, aplicación aparte) no está aquí: vive en el icono de la barra
    // superior — ver `lib/marketing.ts`.
    label: 'MARKETING',
    labelKey: 'sidebar.groups.marketing',
    items: [
      { to: '/marketing', label: 'Resumen', labelKey: 'sidebar.items.marketingOverview', icon: Megaphone, accent: 'fuchsia' },
      { to: '/marketing/campaigns', label: 'Campañas', labelKey: 'sidebar.items.marketingCampaigns', icon: Send, accent: 'fuchsia' },
      { to: '/marketing/templates', label: 'Plantillas', labelKey: 'sidebar.items.marketingTemplates', icon: FileText, accent: 'fuchsia' },
      { to: '/marketing/suppressions', label: 'Bajas', labelKey: 'sidebar.items.marketingSuppressions', icon: Ban, accent: 'fuchsia' },
    ],
  },
  {
    // Absorbe la antigua AGENDA: dos items no justificaban un grupo propio.
    label: 'GESTIÓN',
    labelKey: 'sidebar.groups.management',
    items: [
      { to: '/clients', label: 'Clientes', labelKey: 'sidebar.items.clients', icon: Users, accent: 'sky' },
      { to: '/opportunities', label: 'Oportunidades', labelKey: 'sidebar.items.opportunities', icon: Target, accent: 'rose' },
      { to: '/projects', label: 'Proyectos', labelKey: 'sidebar.items.projects', icon: Briefcase, accent: 'emerald' },
      { to: '/documents', label: 'Documentos', labelKey: 'sidebar.items.documents', icon: FileText, accent: 'blue' },
      { to: '/agenda', label: 'Calendario', labelKey: 'sidebar.items.calendar', icon: Calendar, accent: 'emerald' },
      { to: '/tasks', label: 'Tareas', labelKey: 'sidebar.items.tasks', icon: CheckSquare, accent: 'amber' },
    ],
  },
  {
    // Antes "ADMINISTRACIÓN", que no decía nada de lo que hay dentro.
    label: 'FACTURACIÓN',
    labelKey: 'sidebar.groups.billing',
    items: [
      { to: '/quotes', label: 'Presupuestos', labelKey: 'sidebar.items.quotes', icon: FileSignature, accent: 'orange' },
      // Renombrada: había DOS entradas llamadas "Facturación" en dos grupos.
      { to: '/billing', label: 'Facturas emitidas', labelKey: 'sidebar.items.issuedInvoices', icon: Receipt, accent: 'pink', contabilidad: true },
      { to: '/expenses', label: 'Gastos', labelKey: 'sidebar.items.expenses', icon: Wallet, accent: 'amber', contabilidad: true },
      // Cuelga de Gastos: es la misma entidad entrando por OCR (Expense 1:1
      // con su Document). Indentarla lo dice sin fusionar las dos tablas.
      { to: '/ocr', label: 'Facturas recibidas', labelKey: 'sidebar.items.receivedInvoices', icon: ScanText, accent: 'cyan', contabilidad: true, child: true },
      // También cuelga de Gastos: la hoja es el sobre mensual del que salen
      // gastos, no una entidad distinta. Cada línea aprobada acaba en /expenses.
      { to: '/expense-sheets', label: 'Hojas de gastos', labelKey: 'sidebar.items.expenseSheets', icon: ClipboardList, accent: 'orange', contabilidad: true, child: true },
      { to: '/settings/recurring', label: 'Pagos recurrentes', labelKey: 'settings:nav.recurring', icon: RotateCw, accent: 'rose', adminOnly: true },
      { to: '/rates', label: 'Tarifas', labelKey: 'sidebar.items.rates', icon: Coins, accent: 'blue' },
    ],
  },
  {
    label: 'CONTABILIDAD',
    labelKey: 'sidebar.groups.accounting',
    items: [
      { to: '/accounting/journal', label: 'Diario', labelKey: 'sidebar.items.journal', icon: BookOpen, accent: 'indigo', contabilidad: true },
      { to: '/accounting/ledger', label: 'Libro Mayor', labelKey: 'sidebar.items.ledger', icon: BookOpen, accent: 'indigo', contabilidad: true, child: true },
      { to: '/accounting/trial-balance', label: 'Sumas y saldos', labelKey: 'sidebar.items.trialBalance', icon: Calculator, accent: 'sky', contabilidad: true, child: true },
      { to: '/accounting/pnl', label: 'Cuenta de Resultados', labelKey: 'sidebar.items.pnl', icon: Calculator, accent: 'emerald', contabilidad: true },
      { to: '/accounting/balance', label: 'Balance de situación', labelKey: 'sidebar.items.balance', icon: Calculator, accent: 'sky', contabilidad: true, child: true },
      // Renombrada: "Cuentas anuales" en España es el juego de cinco, y esta
      // página solo trae dos. Quien buscara el balance aquí no lo encontraba.
      { to: '/accounting/annual', label: 'ECPN y Flujos de efectivo', labelKey: 'sidebar.items.annualAccounts', icon: Scale, accent: 'indigo', contabilidad: true, child: true },
      { to: '/accounting/kpis', label: 'KPIs financieros', labelKey: 'sidebar.items.financialKpis', icon: TrendingUp, accent: 'emerald', contabilidad: true, child: true },
      { to: '/accounting/budget', label: 'Presupuesto', labelKey: 'sidebar.items.budget', icon: Target, accent: 'emerald', contabilidad: true },
      { to: '/accounting/aeat', label: 'Modelos AEAT', labelKey: 'sidebar.items.aeat', icon: Calculator, accent: 'fuchsia', contabilidad: true },
      { to: '/banking', label: 'Tesorería', labelKey: 'sidebar.items.banking', icon: Landmark, accent: 'sky', contabilidad: true },
      { to: '/fixed-assets', label: 'Inmovilizado', labelKey: 'sidebar.items.fixedAssets', icon: Boxes, accent: 'amber', contabilidad: true },
      { to: '/accounting/close', label: 'Cierre', labelKey: 'sidebar.items.close', icon: BookCheck, accent: 'indigo' },
      { to: '/accounting/audit', label: 'Auditoría', labelKey: 'sidebar.items.audit', icon: ShieldCheck, accent: 'rose' },
    ],
  },
  {
    label: 'AJUSTES',
    labelKey: 'sidebar.groups.settings',
    items: [
      { to: '/settings/profile', label: 'Mi perfil', labelKey: 'settings:nav.profile', icon: UserCircle, accent: 'toga', contabilidad: true },
      { to: '/settings/security', label: 'Seguridad', labelKey: 'settings:nav.security', icon: ShieldCheck, accent: 'emerald', contabilidad: true, child: true },
      { to: '/settings/users', label: 'Usuarios', labelKey: 'settings:nav.users', icon: Users, accent: 'sky', adminOnly: true },
      // Los 6 catálogos, en una página con pestañas: son datos maestros de
      // visita rara, todos adminOnly, y agruparlos hace evidente que el
      // Categorizador depende de las Tipologías. Sus rutas siguen vivas.
      { to: '/settings/catalogs', label: 'Catálogos', labelKey: 'settings:nav.catalogs', icon: Package, accent: 'orange', adminOnly: true },
      { to: '/settings/firm', label: 'Compañía', labelKey: 'settings:nav.firm', icon: Building2, accent: 'toga', adminOnly: true },
      { to: '/settings/billing', label: 'Series y emisores', labelKey: 'settings:nav.series', icon: Receipt, accent: 'pink', adminOnly: true, child: true },
      { to: '/settings/email', label: 'Correo saliente', labelKey: 'settings:nav.email', icon: Send, accent: 'sky', adminOnly: true, child: true },
      { to: '/settings/integrations', label: 'Integraciones', labelKey: 'settings:nav.integrations', icon: Plug, accent: 'purple', adminOnly: true, child: true },
      { to: '/settings/templates', label: 'Plantillas', labelKey: 'settings:nav.templates', icon: FileText, accent: 'blue', adminOnly: true, child: true },
    ],
  },
]

/**
 * Aplana el árbol dejando solo lo que ESE usuario puede ver. Misma regla que
 * pinta el Sidebar: `adminOnly` es solo para ADMIN y el rol CONTABILIDAD ve una
 * whitelist. Si el menú no lo enseña, el buscador tampoco debe llevarte allí.
 */
export function visibleNavItems(role: string | null): Array<NavItemDef & { group: string }> {
  const isAdmin = role === 'ADMIN'
  const isContabilidad = role === 'CONTABILIDAD'
  return groups.flatMap((g) =>
    g.items
      .filter((it) => (it.adminOnly ? isAdmin : true))
      .filter((it) => (isContabilidad ? it.contabilidad === true : true))
      .map((it) => ({ ...it, group: g.label }))
  )
}
